import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import toast from 'react-hot-toast';
import i18next from 'i18next';
import type { Room, Booking, TimeSlot, CreateBookingData, UpdateBookingData } from '../types';

// Query Keys
export const roomBookingKeys = {
  all: ['room-booking'] as const,
  rooms: () => [...roomBookingKeys.all, 'rooms'] as const,
  room: (id: string) => [...roomBookingKeys.all, 'room', id] as const,
  bookings: () => [...roomBookingKeys.all, 'bookings'] as const,
  myBookings: () => [...roomBookingKeys.all, 'my-bookings'] as const,
  availableSlots: (roomId: string, date: string, duration?: number) =>
    [...roomBookingKeys.all, 'available-slots', roomId, date, duration || 60] as const,
};

/**
 * Hook to fetch all active rooms
 * @returns Query result with rooms array
 */
export function useRooms() {
  return useQuery({
    queryKey: roomBookingKeys.rooms(),
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; data: { rooms: Room[]; total: number; page: number; limit: number; hasMore: boolean } }>('/rooms?isActive=true');
      return data.data.rooms;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a single room by ID
 * @param roomId - Room ID
 * @returns Query result with room data
 */
export function useRoom(roomId: string) {
  return useQuery({
    queryKey: roomBookingKeys.room(roomId),
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; data: Room }>(`/rooms/${roomId}`);
      return data.data;
    },
    enabled: !!roomId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch all bookings (admin only)
 * @param filters - Optional filters for bookings
 * @returns Query result with bookings array
 */
export function useBookings(filters?: {
  roomId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}) {
  return useQuery({
    queryKey: [...roomBookingKeys.bookings(), filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.roomId) params.append('roomId', filters.roomId);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      const { data } = await api.get<{ success: boolean; data: Booking[] }>(
        `/bookings?${params.toString()}`
      );
      return data.data || [];
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: 2,
  });
}

/**
 * Hook to fetch current user's bookings
 * Uses dedicated /bookings/me endpoint that ALWAYS filters by authenticated user
 * @returns Query result with user's bookings
 */
export function useMyBookings() {
  return useQuery({
    queryKey: roomBookingKeys.myBookings(),
    queryFn: async () => {
      console.log('🔍 useMyBookings: Fetching bookings from /bookings/me');
      try {
        const { data } = await api.get<{ success: boolean; data: Booking[] }>('/bookings/me');
        console.log('✅ useMyBookings: Received response:', data);
        const bookings = data.data || [];
        console.log(`📚 useMyBookings: Returning ${bookings.length} bookings for current user`);
        return bookings;
      } catch (error) {
        console.error('❌ useMyBookings: Error fetching bookings:', error);
        throw error;
      }
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: 2,
  });
}

/**
 * Hook to fetch available time slots for a room on a specific date
 * @param roomId - Room ID
 * @param date - Date in YYYY-MM-DD format
 * @param duration - Booking duration in minutes (30 or 60, defaults to 30)
 * @returns Query result with available slots
 */
export function useAvailableSlots(roomId: string, date: string, duration: 30 | 60 = 30) {
  return useQuery({
    queryKey: roomBookingKeys.availableSlots(roomId, date, duration),
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; data: TimeSlot[] }>(
        `/bookings/availability/${roomId}/${date}?duration=${duration}`
      );
      return data.data;
    },
    enabled: !!roomId && !!date,
    staleTime: 30 * 1000, // 30 seconds (slots change frequently)
  });
}

/**
 * Mutation hook to create a new booking
 * @returns Mutation object with mutate function
 */
export function useCreateBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookingData: CreateBookingData) => {
      const { data } = await api.post<{ success: boolean; data: Booking }>(
        '/bookings',
        bookingData
      );
      return data.data;
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: roomBookingKeys.myBookings() });
      queryClient.invalidateQueries({ queryKey: roomBookingKeys.bookings() });
      queryClient.invalidateQueries({
        queryKey: roomBookingKeys.availableSlots(data.roomId, data.date)
      });

      // Success message now handled by individual wizards with custom bell notification
      // const message = data.room?.requiresApproval
      //   ? i18next.t('bookings:hooks.toasts.bookingSubmittedForApproval')
      //   : i18next.t('bookings:hooks.toasts.bookingCreatedSuccess');
      //
      // toast.success(message, {
      //   duration: 4000,
      //   position: 'top-right',
      // });
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || i18next.t('bookings:hooks.toasts.bookingCreateFailed');
      toast.error(message, {
        duration: 5000,
        position: 'top-right',
      });
    },
  });
}

/**
 * Mutation hook to update booking status (admin only)
 * @returns Mutation object with mutate function
 */
export function useUpdateBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      bookingId,
      updateData
    }: {
      bookingId: string;
      updateData: UpdateBookingData;
    }) => {
      const { data } = await api.patch<{ success: boolean; data: Booking }>(
        `/bookings/${bookingId}`,
        updateData
      );
      return data.data;
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: roomBookingKeys.myBookings() });
      queryClient.invalidateQueries({ queryKey: roomBookingKeys.bookings() });
      queryClient.invalidateQueries({
        queryKey: roomBookingKeys.availableSlots(data.roomId, data.date)
      });

      toast.success(i18next.t('bookings:hooks.toasts.bookingUpdatedSuccess'), {
        duration: 3000,
        position: 'top-right',
      });
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || i18next.t('bookings:hooks.toasts.bookingUpdateFailed');
      toast.error(message, {
        duration: 4000,
        position: 'top-right',
      });
    },
  });
}

/**
 * Mutation hook to cancel a booking
 * @returns Mutation object with mutate function
 */
export function useCancelBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookingId: string) => {
      const { data } = await api.delete<{ success: boolean; message: string }>(
        `/bookings/${bookingId}`
      );
      return data;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: roomBookingKeys.myBookings() });
      queryClient.invalidateQueries({ queryKey: roomBookingKeys.bookings() });

      toast.success(i18next.t('bookings:hooks.toasts.bookingCancelledSuccess'), {
        duration: 3000,
        position: 'top-right',
      });
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || i18next.t('bookings:hooks.toasts.bookingCancelFailed');
      toast.error(message, {
        duration: 4000,
        position: 'top-right',
      });
    },
  });
}

// Export types for use in components
// Re-export types for backwards compatibility with public API
export type { Room, Booking, TimeSlot, CreateBookingData, UpdateBookingData } from '../types';
